import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, FilePlus2, MoreHorizontal, Edit, Copy, Eye, UploadCloud,
  Archive, History, Trash2, RotateCcw, LayoutGrid, FileText, CheckCircle2, FileEdit, CalendarClock,
  Monitor, Tablet, Smartphone, Plug,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import KPICard from "@/components/common/KPICard";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { FormField } from "@/components/common/FormSection";
import FormRenderer from "@/components/forms/FormRenderer";
import { ROLE_CHOICES } from "@/lib/formEngine";
import FormBuilderTabs from "@/components/forms/FormBuilderTabs";
import { api, formatApiError } from "@/lib/api";

const EMPTY_CREATE = { name: "", category: "", module: "", description: "" };
const DEVICES = { desktop: "100%", tablet: "768px", mobile: "390px" };

export default function FormLibrary() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [forms, setForms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [preview, setPreview] = useState({ open: false, form: null, definition: null, role: "admin", device: "desktop", values: {} });
  const [history, setHistory] = useState({ open: false, form: null, versions: [] });
  const [placement, setPlacement] = useState({ open: false, form: null, module: "", enabled: false });
  const [confirm, setConfirm] = useState({ open: false });

  const catLabel = useCallback((code) => categories.find((c) => c.code === code)?.label || code, [categories]);

  async function loadStats() {
    try { const r = await api.get("/forms/stats"); setStats(r.data); } catch (e) { /* ignore */ }
  }
  const loadForms = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (catFilter !== "all") params.category = catFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/forms", { params });
      setForms(r.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q, catFilter, statusFilter]);

  useEffect(() => {
    loadStats();
    api.get("/forms/categories").then((r) => setCategories(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(loadForms, 250); return () => clearTimeout(t); }, [loadForms]);

  async function doCreate() {
    if (!createForm.name || !createForm.category) { toast.error("Name and category are required"); return; }
    setCreating(true);
    try {
      const r = await api.post("/forms", createForm);
      toast.success("Form created");
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      navigate(`/forms/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  }

  async function doClone(f) {
    try { await api.post(`/forms/${f.id}/clone`); toast.success("Form cloned"); loadForms(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doPublish(f) {
    try { await api.post(`/forms/${f.id}/publish`); toast.success("Form published"); loadForms(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doRetire(f) {
    try { await api.post(`/forms/${f.id}/retire`); toast.success("Form retired"); loadForms(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doDelete(f) {
    try { await api.delete(`/forms/${f.id}`); toast.success("Form deleted"); loadForms(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doRollback(f, version) {
    try { await api.post(`/forms/${f.id}/rollback?target_version=${version}`); toast.success(`Rolled back to v${version}`); setHistory({ open: false }); loadForms(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function openPreview(f) {
    try {
      const r = await api.get(`/forms/${f.id}`);
      setPreview({ open: true, form: f, definition: r.data.working_version?.definition || { sections: [] }, role: "admin", device: "desktop", values: {} });
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function openHistory(f) {
    try {
      const r = await api.get(`/forms/${f.id}/versions`);
      setHistory({ open: true, form: f, versions: r.data.items });
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function doSetPlacement() {
    try {
      await api.put(`/forms/${placement.form.id}/placement`, { module: placement.module || null, enabled: placement.enabled });
      toast.success("Placement updated");
      setPlacement({ open: false, form: null, module: "", enabled: false });
      loadForms();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="form-library-page">
      <PageHeader
        title="Form Builder"
        subtitle="No-code engine to create, version, and publish forms across the platform."
        actions={
          <Button onClick={() => setCreateOpen(true)} data-testid="create-form-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Form
          </Button>
        }
      />

      <FormBuilderTabs active="clinical" />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6 mt-4">
        <KPICard icon={LayoutGrid} label="Total Forms" value={stats?.total ?? "—"} testId="kpi-total" />
        <KPICard icon={CheckCircle2} label="Published" value={stats?.published ?? "—"} accent="#10B981" testId="kpi-published" />
        <KPICard icon={FileEdit} label="Drafts" value={stats?.draft ?? "—"} accent="#F59E0B" testId="kpi-draft" />
        <KPICard icon={Archive} label="Retired" value={stats?.retired ?? "—"} accent="#94A3B8" testId="kpi-retired" />
        <KPICard icon={CalendarClock} label="Modified (Month)" value={stats?.modified_this_month ?? "—"} accent="#6366F1" testId="kpi-modified" />
      </div>

      {/* Filters */}
      <div className="hah-card p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg px-3 py-1.5 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search forms…" className="bg-transparent outline-none text-sm w-full" data-testid="form-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[200px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="hah-card overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA]">
            <tr>
              {["Form", "Category", "Module", "Version", "Status", "Submissions", ""].map((h) => (
                <th key={h} className="text-left text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {forms.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B7280]" data-testid="no-forms">No forms found.</td></tr>}
            {forms.map((f) => (
              <tr key={f.id} onClick={() => navigate(`/forms/${f.id}`)} className="border-t border-[#E5E7EB] hover:bg-[#EAF8FA]/30 cursor-pointer" data-testid={`form-row-${f.key}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center"><FileText className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <button onClick={() => navigate(`/forms/${f.id}`)} className="font-medium text-[#1F2937] hover:text-[#0B7E8C] truncate text-left" data-testid={`form-name-${f.key}`}>{f.name}</button>
                      <div className="text-xs text-[#94A3B8] font-mono">{f.key}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#475569]">{catLabel(f.category)}</td>
                <td className="px-4 py-3 text-[#94A3B8]">{f.module || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">
                  v{f.published_version || f.latest_version}
                  {f.latest_version > (f.published_version || 0) && <span className="ml-1 text-[10px] text-[#F59E0B]">(draft v{f.latest_version})</span>}
                </td>
                <td className="px-4 py-3"><StatusBadge status={f.status} /></td>
                <td className="px-4 py-3 text-[#475569]">
                  {f.submission_count}
                  {f.placement_enabled && <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#15803D]" data-testid={`placement-badge-${f.key}`}><Plug className="w-3 h-3" /> {f.placement_module}</span>}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`form-actions-${f.key}`} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center"><MoreHorizontal className="w-4 h-4 text-[#6B7280]" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/forms/${f.id}`)} data-testid={`action-edit-${f.key}`}><Edit className="w-4 h-4 mr-2" /> {f.can_write ? "Edit" : "View"}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openPreview(f)} data-testid={`action-preview-${f.key}`}><Eye className="w-4 h-4 mr-2" /> Preview</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openHistory(f)} data-testid={`action-history-${f.key}`}><History className="w-4 h-4 mr-2" /> Version History</DropdownMenuItem>
                      {f.can_write && <DropdownMenuSeparator />}
                      {f.can_write && <DropdownMenuItem onClick={() => doClone(f)} data-testid={`action-clone-${f.key}`}><Copy className="w-4 h-4 mr-2" /> Clone</DropdownMenuItem>}
                      {f.can_write && <DropdownMenuItem onClick={() => setPlacement({ open: true, form: f, module: f.placement_module || "", enabled: !!f.placement_enabled })} data-testid={`action-placement-${f.key}`}><Plug className="w-4 h-4 mr-2" /> Placement</DropdownMenuItem>}
                      {f.can_write && f.latest_version > (f.published_version || 0) && (
                        <DropdownMenuItem onClick={() => setConfirm({ open: true, title: "Publish form", description: `Publish draft v${f.latest_version} of "${f.name}"? Any currently published version will be archived.`, onConfirm: () => doPublish(f) })} data-testid={`action-publish-${f.key}`}><UploadCloud className="w-4 h-4 mr-2" /> Publish draft</DropdownMenuItem>
                      )}
                      {f.can_write && f.status === "published" && (
                        <DropdownMenuItem onClick={() => setConfirm({ open: true, title: "Retire form", description: `Retire "${f.name}"? It will no longer accept submissions.`, onConfirm: () => doRetire(f) })} data-testid={`action-retire-${f.key}`}><Archive className="w-4 h-4 mr-2" /> Retire</DropdownMenuItem>
                      )}
                      {f.can_write && f.status !== "published" && f.submission_count === 0 && (
                        <DropdownMenuItem className="text-[#EF4444]" onClick={() => setConfirm({ open: true, title: "Delete form", description: `Delete "${f.name}"? This cannot be undone.`, destructive: true, onConfirm: () => doDelete(f) })} data-testid={`action-delete-${f.key}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="create-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FilePlus2 className="w-5 h-5 text-[#0B7E8C]" /> New Form</DialogTitle>
            <DialogDescription>Create a form definition. You'll design its fields next.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <FormField label="Form name" required>
              <Input data-testid="create-form-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Follow-Up Assessment" />
            </FormField>
            <FormField label="Category" required>
              <Select value={createForm.category} onValueChange={(v) => setCreateForm({ ...createForm, category: v })}>
                <SelectTrigger data-testid="create-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Related module" hint="Optional — where this form is used (e.g. onboarding, fleet).">
              <Input data-testid="create-form-module" value={createForm.module} onChange={(e) => setCreateForm({ ...createForm, module: e.target.value })} />
            </FormField>
            <FormField label="Description">
              <Textarea data-testid="create-form-description" value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={doCreate} disabled={creating} data-testid="create-form-submit" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">{creating ? "Creating…" : "Create & Design"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview sheet */}
      <Sheet open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}>
        <SheetContent side="right" className="w-full sm:max-w-[760px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>Preview · {preview.form?.name}</SheetTitle>
            <SheetDescription>Live preview using the latest version definition.</SheetDescription>
          </SheetHeader>
          <div className="px-6 py-3 border-b border-[#E5E7EB] bg-white flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              {Object.keys(DEVICES).map((d) => {
                const Icon = d === "desktop" ? Monitor : d === "tablet" ? Tablet : Smartphone;
                return (
                  <button key={d} onClick={() => setPreview((p) => ({ ...p, device: d }))} data-testid={`preview-device-${d}`}
                    className={`p-2 rounded-md ${preview.device === d ? "bg-[#0B7E8C] text-white" : "bg-[#F1F5F9] text-[#64748B]"}`}><Icon className="w-4 h-4" /></button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6B7280]">Preview as role:</span>
              <Select value={preview.role} onValueChange={(v) => setPreview((p) => ({ ...p, role: v }))}>
                <SelectTrigger className="w-[200px] h-9" data-testid="preview-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_CHOICES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="p-6 flex justify-center">
            <div style={{ width: DEVICES[preview.device], maxWidth: "100%" }} className="transition-all">
              {preview.definition && <FormRenderer definition={preview.definition} role={preview.role} values={preview.values} onChange={(v) => setPreview((p) => ({ ...p, values: v }))} />}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Version history sheet */}
      <Sheet open={history.open} onOpenChange={(o) => setHistory((h) => ({ ...h, open: o }))}>
        <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>Version History · {history.form?.name}</SheetTitle>
            <SheetDescription>Every version is preserved. Rollback creates a new published version.</SheetDescription>
          </SheetHeader>
          <div className="p-6 space-y-3">
            {history.versions.map((v) => (
              <div key={v.id} className="hah-card p-4 flex items-start justify-between gap-3" data-testid={`version-row-${v.version}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#1F2937]">Version {v.version}</span>
                    <StatusBadge status={v.status} />
                  </div>
                  <div className="text-xs text-[#6B7280] mt-1">{v.note || "—"}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-1">{(v.published_at || v.created_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
                {history.form?.can_write && v.status !== "published" && (
                  <Button size="sm" variant="outline" className="border-[#E5E7EB]" data-testid={`rollback-${v.version}`}
                    onClick={() => setConfirm({ open: true, title: "Rollback", description: `Restore version ${v.version} as a new published version?`, onConfirm: () => doRollback(history.form, v.version) })}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Rollback
                  </Button>
                )}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Placement dialog */}
      <Dialog open={placement.open} onOpenChange={(o) => setPlacement((p) => ({ ...p, open: o }))}>
        <DialogContent className="sm:max-w-[460px]" data-testid="placement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plug className="w-5 h-5 text-[#0B7E8C]" /> Form Placement</DialogTitle>
            <DialogDescription>Surface "{placement.form?.name}" inside a host module. Off by default — host modules stay unchanged until enabled.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <FormField label="Host module" hint="Where the form appears for users to fill.">
              <Select value={placement.module || "none"} onValueChange={(v) => setPlacement((p) => ({ ...p, module: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="placement-module"><SelectValue placeholder="Select module" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  <SelectItem value="onboarding">Onboarding (request detail)</SelectItem>
                  <SelectItem value="fleet">Fleet (visit detail)</SelectItem>
                  <SelectItem value="patient">Patient (clinical profile)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={placement.enabled} onCheckedChange={(v) => setPlacement((p) => ({ ...p, enabled: v }))} data-testid="placement-enabled" />
              <span><span className="font-medium text-[#1F2937]">Enabled</span><span className="block text-xs text-[#6B7280]">When on, the form appears in the selected module for matching records.</span></span>
            </label>
            {placement.enabled && placement.form?.status !== "published" && (
              <div className="text-xs text-[#B45309] bg-[#FEF3C7] rounded-md px-3 py-2">Note: only published forms render in host modules. Publish this form for it to appear.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlacement((p) => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={doSetPlacement} data-testid="placement-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
